import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

import { PageContainer } from '../../../common/components/PageContainer';
import { useTabParam } from '../../../common/hooks/use-tab-param.hook';
import { AdSuggestion, useAdsApi } from '../../../common/hooks/use-ads-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { ConnectFirst, PageHeading, ProviderMark, formatMoney, readApiError, useAdConnections } from './ads-shared';

const TABS = ['open', 'applied', 'dismissed'] as const;
type Tab = (typeof TABS)[number];

const SuggestionsBody = () => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const connections = useAdConnections();
    const [params] = useSearchParams();
    const focus = params.get('focus');
    const [tab, setTab] = useTabParam(TABS);
    const [items, setItems] = useState<AdSuggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [confirming, setConfirming] = useState<AdSuggestion | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = async (which: Tab = tab) => {
        setLoading(true);
        try {
            setItems(await api.getSuggestions(which));
        } catch (error) {
            console.error('Error loading ad suggestions:', error);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (connections.hasConnection) load(tab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, connections.hasConnection]);

    useEffect(() => {
        if (!focus || loading) return;
        document.getElementById(`suggestion-${focus}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [focus, loading]);

    const refresh = async () => {
        setRefreshing(true);
        try {
            const result = await api.refreshSuggestions();
            setTab('open');
            setItems(result.suggestions);
            toast.success(result.found
                ? t('ADS.SUGGESTIONS.FOUND', '{{count}} suggestion(s) from your latest numbers.', { count: result.found })
                : t('ADS.SUGGESTIONS.NONE_FOUND', 'Nothing needs changing right now.'));
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.SUGGESTIONS.REFRESH_FAILED', 'The checks could not run.')));
        } finally {
            setRefreshing(false);
        }
    };

    const apply = async (suggestion: AdSuggestion) => {
        setBusy(suggestion.uuid);
        try {
            const result = await api.applySuggestion(suggestion.uuid);
            toast.success(result.note || t('ADS.SUGGESTIONS.APPLIED', 'Done.'));
            setConfirming(null);
            load('open');
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.SUGGESTIONS.APPLY_FAILED', 'It could not be applied.')));
            setConfirming(null);
            load('open');
        } finally {
            setBusy(null);
        }
    };

    const dismiss = async (suggestion: AdSuggestion) => {
        setBusy(suggestion.uuid);
        try {
            await api.dismissSuggestion(suggestion.uuid);
            setItems((current) => current.filter((s) => s.uuid !== suggestion.uuid));
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.SUGGESTIONS.DISMISS_FAILED', 'It could not be dismissed.')));
        } finally {
            setBusy(null);
        }
    };

    const heading = (
        <PageHeading
            title={t('ADS.SUGGESTIONS.TITLE', 'Suggestions')}
            subtitle={t(
                'ADS.SUGGESTIONS.SUBTITLE',
                'Worked out from your spending and what happened on your site. Nothing changes in your ad accounts until you apply it.',
            )}
        >
            {connections.hasConnection && (
                <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
                    {refreshing ? <Spinner data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                    {t('ADS.SUGGESTIONS.REFRESH', 'Check again')}
                </Button>
            )}
        </PageHeading>
    );

    if (connections.loading) return <div className="flex flex-col gap-6">{heading}<Skeleton className="h-64 w-full" /></div>;
    if (!connections.hasConnection) return <div className="flex flex-col gap-6">{heading}<ConnectFirst /></div>;

    return (
        <div className="flex flex-col gap-6">
            {heading}

            <ToggleGroup type="single" variant="outline" size="sm" value={tab} onValueChange={(v) => v && setTab(v as Tab)} className="w-fit">
                <ToggleGroupItem value="open">{t('ADS.SUGGESTIONS.TAB_OPEN', 'To review')}</ToggleGroupItem>
                <ToggleGroupItem value="applied">{t('ADS.SUGGESTIONS.TAB_APPLIED', 'Applied')}</ToggleGroupItem>
                <ToggleGroupItem value="dismissed">{t('ADS.SUGGESTIONS.TAB_DISMISSED', 'Dismissed')}</ToggleGroupItem>
            </ToggleGroup>

            {loading ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Skeleton className="h-48" /><Skeleton className="h-48" />
                </div>
            ) : !items.length ? (
                <Empty className="border">
                    <EmptyHeader>
                        <EmptyMedia variant="icon">{tab === 'open' ? <CheckCircle2 /> : <Sparkles />}</EmptyMedia>
                        <EmptyTitle>
                            {tab === 'open'
                                ? t('ADS.SUGGESTIONS.EMPTY_OPEN_TITLE', 'Nothing to change right now')
                                : t('ADS.SUGGESTIONS.EMPTY_OTHER_TITLE', 'Nothing here yet')}
                        </EmptyTitle>
                        <EmptyDescription>
                            {tab === 'open'
                                ? t('ADS.SUGGESTIONS.EMPTY_OPEN_BODY', 'Checks run after every sync. Press Check again to run them now.')
                                : t('ADS.SUGGESTIONS.EMPTY_OTHER_BODY', 'Suggestions you apply or dismiss are kept here.')}
                        </EmptyDescription>
                    </EmptyHeader>
                </Empty>
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {items.map((s) => (
                        <SuggestionCard
                            key={s.uuid}
                            suggestion={s}
                            focused={s.uuid === focus}
                            busy={busy === s.uuid}
                            onApply={() => setConfirming(s)}
                            onDismiss={() => dismiss(s)}
                        />
                    ))}
                </div>
            )}

            <AlertDialog open={!!confirming} onOpenChange={(o) => !o && !busy && setConfirming(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirming?.title}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('ADS.SUGGESTIONS.CONFIRM_BODY', 'This changes your ad account now. You can undo it from the Campaigns page.')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={!!busy}>{t('ADS.COMMON.CANCEL', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction disabled={!!busy} onClick={(e) => { e.preventDefault(); if (confirming) apply(confirming); }}>
                            {busy && <Spinner data-icon="inline-start" />}
                            {t('ADS.SUGGESTIONS.APPLY', 'Apply')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const SuggestionCard = ({
    suggestion: s, focused, busy, onApply, onDismiss,
}: { suggestion: AdSuggestion; focused: boolean; busy: boolean; onApply: () => void; onDismiss: () => void }) => {
    const { t } = useTranslation();
    const kinds: Record<string, string> = {
        pause_campaign: t('ADS.KIND.PAUSE', 'Stop wasted spend'),
        shift_budget: t('ADS.KIND.SHIFT', 'Move budget'),
        set_budget: t('ADS.KIND.SET_BUDGET', 'Budget'),
        create_audience: t('ADS.KIND.AUDIENCE', 'New audience'),
        promote_product: t('ADS.KIND.PROMOTE', 'Product to advertise'),
        cover_demand: t('ADS.KIND.DEMAND', 'Missed demand'),
        refresh_creative: t('ADS.KIND.CREATIVE', 'Creative'),
        budget_pacing: t('ADS.KIND.PACING', 'Monthly budget'),
    };
    const evidence = Object.entries(s.evidence ?? {}).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object');

    return (
        <Card id={`suggestion-${s.uuid}`} className={cn(focused && 'ring-2 ring-foreground')}>
            <CardHeader>
                <CardDescription className="flex items-center gap-2">
                    {s.provider && <ProviderMark provider={s.provider} />}
                    <span className={cn('size-1.5 rounded-full', s.severity === 'critical' ? 'bg-destructive' : s.severity === 'warning' ? 'bg-warning' : 'bg-muted-foreground/50')} aria-hidden />
                    {kinds[s.kind] ?? s.kind}
                </CardDescription>
                <CardTitle className="leading-snug">{s.title}</CardTitle>
                {s.estimatedMonthlyImpact ? (
                    <CardAction>
                        <Badge variant="secondary" className="tabular-nums">
                            {s.kind === 'pause_campaign'
                                ? t('ADS.SUGGESTIONS.SAVES', 'Saves {{amount}}/mo', { amount: formatMoney(s.estimatedMonthlyImpact, s.currency) })
                                : t('ADS.SUGGESTIONS.WORTH', '≈ {{amount}}/mo', { amount: formatMoney(s.estimatedMonthlyImpact, s.currency) })}
                        </Badge>
                    </CardAction>
                ) : null}
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">{s.detail}</p>
                {evidence.length > 0 && (
                    <Collapsible>
                        <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                            {t('ADS.SUGGESTIONS.EVIDENCE', 'The numbers behind it')}
                            <ChevronDown className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border p-3 text-xs">
                                {evidence.map(([key, value]) => (
                                    <div key={key} className="contents">
                                        <dt className="text-muted-foreground">{key}</dt>
                                        <dd className="text-end tabular-nums">{String(value)}</dd>
                                    </div>
                                ))}
                            </dl>
                        </CollapsibleContent>
                    </Collapsible>
                )}
                {s.resultNote && <p className="rounded-md bg-muted px-3 py-2 text-xs">{s.resultNote}</p>}
            </CardContent>
            {s.status === 'open' && (
                <CardFooter className="justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onDismiss} disabled={busy}>
                        {t('ADS.SUGGESTIONS.NOT_NOW', 'Not now')}
                    </Button>
                    {s.applicable ? (
                        <Button variant="outline" size="sm" onClick={onApply} disabled={busy}>
                            {busy && <Spinner data-icon="inline-start" />}
                            {t('ADS.SUGGESTIONS.APPLY', 'Apply')}
                        </Button>
                    ) : (
                        <span className="text-xs text-muted-foreground">{t('ADS.SUGGESTIONS.ADVICE', 'Advice — nothing to switch on')}</span>
                    )}
                </CardFooter>
            )}
        </Card>
    );
};

export const AdsSuggestionsPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.ADS.TITLE', 'Ads') }, { label: t('SIDEBAR.ADS.SUGGESTIONS', 'Suggestions') }]}>
            <FeatureGate feature="adsManager">
                <SuggestionsBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AdsSuggestionsPage;
